import { useState } from 'react';
import { uploadTrainingDocument, getTrainingDocuments, deleteTrainingDocument } from '../../services/aiTrainingService';

/**
 * Upload Training Document Modal
 * For admin/sales_manager to upload training materials
 */
export function UploadTrainingDocModal({ isOpen, onClose, currentUser, showToast }) {
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  if (!isOpen) return null;

  const loadDocuments = async () => {
    setLoadingDocs(true);
    try {
      const docs = await getTrainingDocuments();
      setDocuments(docs);
    } catch (err) {
      console.error('Failed to load documents:', err);
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleUpload = async () => {
    if (!file || !title) {
      showToast('يرجى اختيار ملف وإدخال العنوان', 'error');
      return;
    }

    setUploading(true);
    try {
      await uploadTrainingDocument(file, title, currentUser);
      showToast('✅ تم رفع المستند بنجاح! جاري المعالجة...');
      setTitle('');
      setFile(null);
      await loadDocuments();
    } catch (err) {
      console.error('Upload error:', err);
      showToast('❌ فشل في رفع المستند', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    try {
      await deleteTrainingDocument(docId);
      showToast('✅ تم حذف المستند');
      await loadDocuments();
    } catch (err) {
      console.error('Delete error:', err);
      showToast('❌ فشل في حذف المستند', 'error');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'قيد الانتظار', color: '#f59e0b', bg: '#3d2000' },
      processing: { label: 'جاري المعالجة', color: '#4ecdc4', bg: '#f0fdfa' },
      ready: { label: 'جاهز', color: '#10b981', bg: '#052e16' },
      failed: { label: 'فشل', color: '#ef4444', bg: '#3a1000' }
    };
    const s = statusMap[status] || statusMap.pending;
    return (
      <span style={{
        background: s.bg,
        color: s.color,
        padding: '3px 10px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700
      }}>
        {s.label}
      </span>
    );
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.78)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 200,
      animation: 'fadeIn 0.2s ease',
      padding: 16
    }}>
      <div style={{
        background: '#ffffff',
        borderRadius: 20,
        padding: 30,
        width: '100%',
        maxWidth: 700,
        maxHeight: '92vh',
        overflowY: 'auto',
        border: '1px solid #e2e8f0',
        boxShadow: '0 40px 100px #000f',
        animation: 'slideIn 0.25s ease'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <h3 style={{ color: '#000000', fontSize: 17, fontWeight: 900 }}>
            📚 إدارة المواد التدريبية
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#64748b',
              fontSize: 24,
              cursor: 'pointer',
              padding: '0 4px'
            }}
          >
            ✕
          </button>
        </div>

        {/* Upload Form */}
        <div style={{
          background: '#ffffff',
          borderRadius: 16,
          padding: 20,
          marginBottom: 20,
          border: '1px solid #e2e8f0'
        }}>
          <h4 style={{ color: '#000000', fontSize: 15, fontWeight: 700, marginBottom: 16 }}>
            📤 رفع مستند جديد
          </h4>
          
          <div style={{ marginBottom: 14 }}>
            <label style={{ color: '#4d6680', fontSize: 11, display: 'block', marginBottom: 6, fontWeight: 700 }}>
              عنوان المستند
            </label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="مثال: دليل المسوق، سياسة المبيعات..."
              style={{
                width: '100%',
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                padding: '10px 14px',
                color: '#000000',
                fontSize: 13,
                fontFamily: "'Cairo',sans-serif",
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ color: '#4d6680', fontSize: 11, display: 'block', marginBottom: 6, fontWeight: 700 }}>
              الملف (PDF, DOC, DOCX, TXT)
            </label>
            <div
              onClick={() => document.getElementById('file-input').click()}
              style={{
                border: '2px dashed #e2e8f0',
                borderRadius: 12,
                padding: 24,
                textAlign: 'center',
                cursor: 'pointer',
                background: file ? '#ffffff' : '#ffffff',
                borderColor: file ? '#10b981' : '#e2e8f0',
                transition: 'all 0.2s'
              }}
            >
              <input
                id="file-input"
                type="file"
                accept=".pdf,.doc,.docx,.txt"
                onChange={e => setFile(e.target.files[0])}
                style={{ display: 'none' }}
              />
              <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
              <div style={{ color: '#000000', fontSize: 13 }}>
                {file ? file.name : 'اضغط لاختيار ملف'}
              </div>
              {file && (
                <div style={{ color: '#10b981', fontSize: 12, marginTop: 4 }}>
                  {(file.size / 1024).toFixed(1)} KB
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleUpload}
            disabled={uploading || !file || !title}
            style={{
              width: '100%',
              background: uploading || !file || !title
                ? '#e2e8f0'
                : 'linear-gradient(135deg, #4ecdc4, #38b2ac)',
              border: 'none',
              borderRadius: 10,
              padding: '12px 18px',
              color: '#fff',
              cursor: uploading || !file || !title ? 'not-allowed' : 'pointer',
              fontFamily: "'Cairo',sans-serif",
              fontSize: 14,
              fontWeight: 700,
              opacity: uploading || !file || !title ? 0.5 : 1
            }}
          >
            {uploading ? '⏳ جاري الرفع...' : '📤 رفع المستند'}
          </button>
        </div>

        {/* Documents List */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h4 style={{ color: '#000000', fontSize: 15, fontWeight: 700, margin: 0 }}>
              📋 المستندات المرفوعة ({documents.length})
            </h4>
            <button
              onClick={loadDocuments}
              style={{
                background: 'transparent',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                padding: '6px 12px',
                color: '#000000',
                cursor: 'pointer',
                fontSize: 12,
                fontFamily: "'Cairo',sans-serif"
              }}
            >
              🔄 تحديث
            </button>
          </div>

          {loadingDocs ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#3d5470' }}>
              جاري التحميل...
            </div>
          ) : documents.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: 40,
              background: '#ffffff',
              borderRadius: 12,
              border: '1px solid #e2e8f0',
              color: '#3d5470'
            }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
              <div style={{ fontSize: 14 }}>لا توجد مستندات بعد</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {documents.map(doc => (
                <div
                  key={doc.id}
                  style={{
                    background: '#ffffff',
                    borderRadius: 12,
                    padding: 16,
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#000000', fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                      {doc.title}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: '#64748b' }}>
                      <span>{doc.file_type?.toUpperCase()}</span>
                      <span>•</span>
                      <span>{new Date(doc.created_at).toLocaleDateString('ar-AE')}</span>
                      <span>•</span>
                      {getStatusBadge(doc.status)}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(doc.id)}
                    style={{
                      background: '#ffffff',
                      border: '1px solid #ef4444',
                      borderRadius: 8,
                      padding: '6px 12px',
                      color: '#ef4444',
                      cursor: 'pointer',
                      fontSize: 12,
                      fontFamily: "'Cairo',sans-serif",
                      fontWeight: 700
                    }}
                  >
                    🗑️ حذف
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
